import {skipToken} from '@reduxjs/toolkit/query';
import {isFeatureEnabled} from 'feature-flags';
import {Card, DataStatus} from 'platform/components';
import {Grid, Show, VStack} from 'platform/foundation';

import {useState} from 'react';

import {isNil, isNotNil} from 'ramda';
import {isFalse} from 'ramda-adjunct';

import {useGetParticipationQuery} from '@dms/api/core';
import {useGetInterestUtmQuery} from '@dms/api/finance';
import {useGetBusinessCaseQuery, usePutBusinessCaseContactPersonMutation} from '@dms/api/sales';
import {EMPTY_UTM_PARAMETERS, EntityResourceIds} from '@dms/api/shared';
import {featureFlags} from '@dms/feature-flags';
import {CustomerInsightsTrigger} from '@dms/features/customer-insights';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {
  CustomerInformationCard,
  handleApiError,
  useBusinessCaseActions,
  usePermissions,
  UtmCard,
} from '@dms/shared';

import {ContactPersonForm} from '../ContactPersonForm';

const UTM_CARD_MIN_HEIGHT = 100;

interface CustomerPanelProps {
  businessCaseId: string;
  openCustomerWidget: () => void;
}

export function CustomerPanel(props: CustomerPanelProps) {
  const {
    data: businessCase,
    isLoading: isLoadingBusinessCase,
    isError: isErrorBusinessCase,
  } = useGetBusinessCaseQuery({businessCaseId: props.businessCaseId});

  // UTM parameters are owned by the interest the business case originated from - read-only here
  const isUtmSectionEnabled = isFeatureEnabled(featureFlags.INTERESTS_UTM);
  const interestId = businessCase?.interestId;
  const {
    data: utm,
    isLoading: isLoadingUtm,
    isError: isErrorUtm,
  } = useGetInterestUtmQuery(
    isUtmSectionEnabled && isNotNil(interestId) ? {id: interestId} : skipToken
  );

  const {isActionEnabled} = useBusinessCaseActions(props.businessCaseId);
  const [putBusinessCaseContactPerson] = usePutBusinessCaseContactPersonMutation();

  const customerId = businessCase?.customerId;
  const isBusinessCaseBuyingOrBrokerage =
    businessCase?.businessCaseType === 'BUYING' ||
    businessCase?.businessCaseType === 'PURCHASE_BROKERAGE';

  const [showForm, setShowForm] = useState(false);

  const {data: businessCaseParticipation} = useGetParticipationQuery({
    recordId: props.businessCaseId,
    resourceId: EntityResourceIds.businessCase,
  });

  const [
    hasChangeBusinessCasePurchaseSellerPermission,
    hasSelectBusinessCasePurchaseSellerPermission,
    hasChangeBusinessCaseSellingBuyerPermission,
    hasSelectBusinessCaseSellingBuyerPermission,
    hasChangeBusinessCaseCustomerPermission,
  ] = usePermissions({
    permissionKeys: [
      'changeBusinessCasePurchaseSeller',
      'selectBusinessCasePurchaseSellerContactPerson',
      'changeBusinessCaseSellingBuyer',
      'selectBusinessCaseSellingBuyerContactPerson',
      'changeBusinessCaseCustomer',
    ],
    scopes: {
      changeBusinessCasePurchaseSeller: {
        participation: businessCaseParticipation,
        branchId: businessCase?.branchId,
      },
      selectBusinessCasePurchaseSellerContactPerson: {
        participation: businessCaseParticipation,
        branchId: businessCase?.branchId,
      },
      changeBusinessCaseSellingBuyer: {
        participation: businessCaseParticipation,
        branchId: businessCase?.branchId,
      },
      selectBusinessCaseSellingBuyerContactPerson: {
        participation: businessCaseParticipation,
        branchId: businessCase?.branchId,
      },
      changeBusinessCaseCustomer: {
        participation: businessCaseParticipation,
        branchId: businessCase?.branchId,
      },
    },
  });

  const canSelectContactPerson = isBusinessCaseBuyingOrBrokerage
    ? hasSelectBusinessCasePurchaseSellerPermission
    : hasSelectBusinessCaseSellingBuyerPermission;

  const canChangeCustomer =
    (isBusinessCaseBuyingOrBrokerage
      ? hasChangeBusinessCasePurchaseSellerPermission
      : hasChangeBusinessCaseSellingBuyerPermission) && hasChangeBusinessCaseCustomerPermission;

  const isSelectContractPersonDisabled =
    !canSelectContactPerson || !isActionEnabled('SELECT_CUSTOMER_CONTACT_PERSON');

  const toggleContactPerson = (value: boolean) => {
    putBusinessCaseContactPerson({
      businessCaseId: props.businessCaseId,
      putBusinessCaseContactPersonRequestBody: {
        // NOTE: with every toggle, we intentionally reset the contact person
        contactId: null,
      },
    })
      .unwrap()
      .then(() => setShowForm(value))
      .catch(handleApiError);
  };

  return (
    <DataStatus
      isLoading={isLoadingBusinessCase}
      isError={isErrorBusinessCase}
      isEmpty={isNil(customerId)}
    >
      <VStack spacing={4}>
        <Grid columns={[1, 2]} align="flex-start">
          <VStack spacing={4}>
            <CustomerInformationCard
              customerId={customerId!}
              resourceId={EntityResourceIds.businessCase}
              recordId={props.businessCaseId}
              onChange={props.openCustomerWidget}
              isDetailLinkVisible
              shouldWarnBeforeActiveBusinessCaseEdit
              isCustomerChangeDisabled={businessCase?.businessCaseState === 'CLOSED'}
              isChangeCustomerForbidden={isFalse(canChangeCustomer)}
              data-testid={testIds.businessCase.customer('customerInformation')}
              headerAction={
                <CustomerInsightsTrigger
                  customerId={customerId}
                  data-testid="businessCase-customerInsights"
                />
              }
            />
            <Show when={isUtmSectionEnabled}>
              <DataStatus
                isLoading={isLoadingUtm}
                isError={isErrorUtm}
                minHeight={UTM_CARD_MIN_HEIGHT}
              >
                <UtmCard
                  parameters={utm ?? EMPTY_UTM_PARAMETERS}
                  data-testid={testIds.businessCase.customer('utm')}
                />
              </DataStatus>
            </Show>
          </VStack>
          <Card
            data-testid={testIds.businessCase.customer('contactPerson')}
            variant="default"
            isExpanded={isNotNil(businessCase?.contactPersonId) || showForm}
            control={{
              type: 'switch',
              value: isNotNil(businessCase?.contactPersonId),
              isDisabled: isSelectContractPersonDisabled,
              onChange: toggleContactPerson,
              'data-testid': testIds.businessCase.customer('toggleContactPerson'),
            }}
            title={i18n.t('entity.businessCase.labels.contactPerson')}
          >
            <Card data-testid={testIds.businessCase.customer('contactPerson')} variant="inlineGrey">
              <ContactPersonForm
                customerId={customerId!}
                isDisabled={isSelectContractPersonDisabled}
                contactPersonId={businessCase?.contactPersonId}
              />
            </Card>
          </Card>
        </Grid>
      </VStack>
    </DataStatus>
  );
}
